import { useMemo, useState } from "react";
import type { AccountingHealth } from "@/api/accounting";
import { Button } from "@/components/ui/button";
import { MappingHealthBanner } from "@/components/accounting/MappingHealthBanner";
import { MappingHealthModal } from "@/components/accounting/MappingHealthModal";
import { useToast } from "@/components/ToastProvider";
import { useShopify } from "@/hooks/useShopify";
import { useLedger } from "@/hooks/useLedger";
import { useAccountingHealth } from "@/hooks/useAccountingHealth";
import { useUnifiedErrorSurface } from "@/hooks/useUnifiedErrorSurface";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

type ShopifyPanelProps = {
  health?: AccountingHealth | null;
  showHealthBanner?: boolean;
  healthLoading?: boolean;
  healthError?: string | null;
};

export function ShopifyPanel({
  health: externalHealth,
  showHealthBanner = true,
  healthLoading,
  healthError,
}: ShopifyPanelProps = {}) {
  const { push } = useToast();
  const { shops, payouts, orders, loading, error, savingShop, postingPayoutId, saveShop, syncPayouts, syncOrders, journalizePayout, refresh } = useShopify();
  const { journals } = useLedger();
  const [form, setForm] = useState({
    shop_domain: "example.myshopify.com",
    access_token: "",
    currency: "USD",
    bank_account_id: 0,
    sales_account_id: 0,
    fee_account_id: 0,
    tax_account_id: 0,
  });
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const [selectedJournalId, setSelectedJournalId] = useState<number | null>(null);
  const [ordersSince, setOrdersSince] = useState<string>("");

  const activeShopId = useMemo(() => selectedShopId ?? shops[0]?.id ?? null, [selectedShopId, shops]);

  const [healthModalOpen, setHealthModalOpen] = useState(false);
  const {
    health: internalHealth,
    loading: internalLoading,
    error: internalError,
    refresh: refreshHealth,
  } = useAccountingHealth(!externalHealth);
  const mergedHealth = externalHealth ?? internalHealth;
  const bannerLoading = healthLoading ?? internalLoading;
  const bannerError = healthError ?? internalError;
  const { notifyError } = useUnifiedErrorSurface();

  const handleConnect = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.shop_domain || !form.access_token) {
      push({ title: "Missing details", message: "Provide domain and access token." });
      return;
    }
    try {
      await saveShop(form);
      push({ title: "Shop saved", message: form.shop_domain });
    } catch (err) {
      notifyError(err);
    }
  };

  const handleJournalize = async (payoutId: string) => {
    if (!selectedJournalId) {
      push({ title: "Select journal", message: "Choose a ledger journal first." });
      return;
    }
    if (mergedHealth && !mergedHealth.ok) {
      push({
        title: "Mappings required",
        message: "Before confirming events, complete your ledger mappings.",
      });
      setHealthModalOpen(true);
      return;
    }
    try {
      await journalizePayout(payoutId, selectedJournalId);
      push({ title: "Payout posted", message: payoutId });
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-6">
      {showHealthBanner && (
        <MappingHealthBanner
          health={mergedHealth}
          loading={bannerLoading}
          error={bannerError}
          onViewRequirements={() => setHealthModalOpen(true)}
        />
      )}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Shopify adapter</h2>
          <p className="text-sm text-muted-foreground">Sync orders and payouts, then journalize deposits.</p>
        </div>
        <Button variant="outline" onClick={() => refresh()} disabled={loading}>
          Refresh
        </Button>
      </div>
      {error && <div className="rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={handleConnect} className="space-y-3 rounded-2xl border border-border bg-background/70 p-5 text-sm">
          <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Connect shop</h3>
          <p className="text-xs text-muted-foreground">Store credentials securely in the backend.</p>
          <label className="flex flex-col gap-1 text-xs">
            <span>Shop domain</span>
            <input
              type="text"
              value={form.shop_domain}
              onChange={(event) => setForm((prev) => ({ ...prev, shop_domain: event.target.value }))}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span>Admin access token</span>
            <input
              type="password"
              value={form.access_token}
              onChange={(event) => setForm((prev) => ({ ...prev, access_token: event.target.value }))}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span>Currency</span>
            <input
              type="text"
              maxLength={3}
              value={form.currency}
              onChange={(event) => setForm((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
          <Button type="submit" disabled={savingShop}>
            {savingShop ? "Saving…" : "Save shop"}
          </Button>
        </form>
        <section className="space-y-3 rounded-2xl border border-border bg-background/70 p-5 text-sm">
          <header>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Connected shops</h3>
            <p className="text-xs text-muted-foreground">Trigger syncs and payouts per shop.</p>
          </header>
          {shops.length === 0 && <p className="text-xs text-muted-foreground">No shops configured yet.</p>}
          {shops.map((shop) => (
            <div
              key={shop.id}
              className={`rounded-xl border px-3 py-3 ${activeShopId === shop.id ? "border-success/60 bg-success/10" : "border-border bg-background/30"}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-foreground">{shop.shop_domain}</div>
                  <div className="text-xs text-muted-foreground">{shop.currency}</div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      try {
                        await syncOrders(shop.id, ordersSince || undefined);
                        push({ title: "Orders synced", message: shop.shop_domain });
                      } catch (err) {
                        notifyError(err);
                      }
                    }}
                  >
                    Sync orders
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      try {
                        await syncPayouts(shop.id);
                        push({ title: "Payouts synced", message: shop.shop_domain });
                      } catch (err) {
                        notifyError(err);
                      }
                    }}
                  >
                    Sync payouts
                  </Button>
                  <Button size="sm" onClick={() => setSelectedShopId(shop.id)}>
                    Manage
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <label className="mt-2 flex flex-col gap-1 text-xs">
            <span>Orders since (ISO date)</span>
            <input
              type="datetime-local"
              value={ordersSince}
              onChange={(event) => setOrdersSince(event.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
          </label>
        </section>
      </div>
      <div className="space-y-4 rounded-2xl border border-border bg-background/70 p-5">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Payouts</h3>
            <p className="text-xs text-muted-foreground">Select a journal then journalize payouts.</p>
          </div>
          <select
            className="rounded-lg border border-border bg-background/40 px-3 py-2 text-sm text-foreground"
            value={selectedJournalId ?? ""}
            onChange={(event) => setSelectedJournalId(Number(event.target.value))}
          >
            <option value="" disabled>
              Select journal
            </option>
            {journals.map((journal) => (
              <option key={journal.id} value={journal.id}>
                {journal.code} · {journal.name}
              </option>
            ))}
          </select>
        </header>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-background/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Payout</th>
                <th className="px-4 py-2 text-left">Shop</th>
                <th className="px-4 py-2 text-right">Gross</th>
                <th className="px-4 py-2 text-right">Fees</th>
                <th className="px-4 py-2 text-right">Net</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout) => (
                <tr key={payout.id} className="border-t border-border">
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{payout.payout_id}</td>
                  <td className="px-4 py-2 text-foreground/80">{shops.find((shop) => shop.id === payout.shop_id)?.shop_domain ?? "—"}</td>
                  <td className="px-4 py-2 text-right text-primary-foreground dark:text-amber-100">{currency.format(Number(payout.gross_amount))}</td>
                  <td className="px-4 py-2 text-right text-destructive">{currency.format(Number(payout.fee_amount))}</td>
                  <td className="px-4 py-2 text-right text-success">{currency.format(Number(payout.net_amount))}</td>
                  <td className="px-4 py-2 text-muted-foreground">{payout.ledger_entry_id ? "Journalized" : payout.status ?? "pending"}</td>
                  <td className="px-4 py-2 text-right">
                    {payout.ledger_entry_id ? (
                      <span className="text-xs text-muted-foreground">Posted</span>
                    ) : (
                      <Button size="sm" onClick={() => handleJournalize(payout.id)} disabled={postingPayoutId === payout.id}>
                        {postingPayoutId === payout.id ? "Posting…" : "Journalize"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <MappingHealthModal
        open={healthModalOpen}
        health={mergedHealth}
        ctaHref="/accounting"
        onClose={() => {
          setHealthModalOpen(false);
          if (!externalHealth) {
            void refreshHealth();
          }
        }}
      />
    </div>
  );
}
